import {
  Emphasis,
  List,
  ListItem,
  Paragraph,
  Stack,
  Strong,
  useConfirmationModal,
} from '@customerio/pluma-components/react';
import { PLATFORM_LABELS, type PushPlatform } from '@/features/push-credentials';

type PushDisableConsequencesProps = {
  /** Null when every platform goes at once. */
  platform: PushPlatform | null;
};

function PushDisableConsequences({ platform }: PushDisableConsequencesProps) {
  const label = platform == null ? null : PLATFORM_LABELS[platform];
  const settings = label == null ? 'push settings' : `${label} push settings`;
  const removing = label == null ? 'push settings' : `${label} settings`;
  const configuration = label == null ? 'push configuration' : `${label} configuration`;
  const messages = label == null ? 'push messages' : `${label} push messages`;

  return (
    <Stack gap="200">
      <Paragraph>
        You're about to <Strong>remove your {settings}</Strong>. This <Strong>cannot</Strong> be undone. Removing{' '}
        {removing} will:
      </Paragraph>

      <List gap="100">
        <ListItem>Delete your {configuration} permanently</ListItem>
        <ListItem>Cause any active {messages} to fail</ListItem>
      </List>
    </Stack>
  );
}

type UseDisablePushConfirmationOptions = {
  /** Null for the whole workspace's push settings. */
  platform: PushPlatform | null;
  /** Runs with the prompt still open; a failure is the caller's to report. */
  onDisable: () => Promise<void>;
};

export function useDisablePushConfirmation({ platform, onDisable }: UseDisablePushConfirmationOptions): () => void {
  const confirm = useConfirmationModal();
  const subject = platform == null ? 'push' : PLATFORM_LABELS[platform];

  return () => {
    void confirm({
      title: `Disable ${subject}?`,
      confirmLabel: `Disable ${subject}`,
      cancelLabel: 'Cancel',
      isCritical: true,
      message: <PushDisableConsequences platform={platform} />,
      onConfirm: async () => {
        try {
          await onDisable();
        } catch {
          // Reported through the page's error list; the prompt closes either way.
        }
      },
    });
  };
}

type SandboxSwitchConsequencesProps = {
  /** The current endpoint, before the switch. */
  isSandbox: boolean;
};

function SandboxSwitchConsequences({ isSandbox }: SandboxSwitchConsequencesProps) {
  return (
    <Paragraph>
      Switching {isSandbox ? 'off' : 'on'} "Send to sandbox" means that any existing <Emphasis>and</Emphasis> future
      push notifications will be sent using the APNs {isSandbox ? 'production' : 'sandbox'} environment. If you're using{' '}
      {isSandbox ? 'sandbox' : 'production'} device tokens, those messages <Strong>will</Strong> fail.
    </Paragraph>
  );
}

type UseSandboxSwitchConfirmationOptions = {
  isSandbox: boolean;
  onSwitch: (sandbox: boolean) => Promise<void>;
};

export function useSandboxSwitchConfirmation({ isSandbox, onSwitch }: UseSandboxSwitchConfirmationOptions): () => void {
  const confirm = useConfirmationModal();
  const target = isSandbox ? 'production' : 'sandbox';

  return () => {
    void confirm(
      {
        title: `Switch to ${target} environment?`,
        confirmLabel: `Switch to ${target} environment`,
        cancelLabel: 'Cancel',
        isCritical: true,
        message: <SandboxSwitchConsequences isSandbox={isSandbox} />,
        onConfirm: async () => {
          try {
            await onSwitch(!isSandbox);
          } catch {
            // Reported through the page's error list; the prompt closes either way.
          }
        },
      },
      { shouldCloseOnUnmount: true },
    );
  };
}
